import { EventEmitter, Injectable } from '@angular/core';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class FirebaseMessagingService {

    /**
     * Triggered when registration of the device succeeds.
     */
    public readonly onRegistration = new EventEmitter<string>();
    /**
     * Triggered when registration of the device fails. The error is included as argument.
     */
    public readonly onRegistrationError = new EventEmitter<any>();
    /**
     * Triggered when a data-only message is received. This message is not shown to the user.
     */
    public readonly onDataMessageReceived = new EventEmitter<PushNotificationSchema>();
    /**
     * Triggered when a message is received that is a push notification (i.e., should be displayed to the user)
     */
    public readonly onPushNotificationReceived = new EventEmitter<PushNotificationSchema>();
    /**
     * Triggered when the user performed an action on the push notification.
     */
    public readonly onPushNotificationActionPerformed = new EventEmitter<ActionPerformed>();
    /**
     * The token received from Firebase (if any)
     */
    private token: string | null = null;
    /**
     * Subject to debounce registration events from Firebase
     */
    private readonly registrationSubject = new Subject<string>();
    /**
     * All handles for the plugin
     */
    private hRegistration: PluginListenerHandle | null = null;
    private hRegistrationError: PluginListenerHandle | null = null;
    private hPushNotificationReceived: PluginListenerHandle | null = null;
    private hPushNotificationActionPerformed: PluginListenerHandle | null = null;

    constructor() {
        // Debounce registration events by 1 second and only emit distinct tokens
        this.registrationSubject.pipe(
            debounceTime(1000),
            distinctUntilChanged()
        ).subscribe(token => {
            this.onRegistration.emit(token);
        });
    }

    /**
     * Initialize the Firebase messaging service. Call init when initializing the application in the AppComponent.
     */
    public async init() {
        if (Capacitor.getPlatform() === 'web') {
            console.log('FirebaseMessageService: not enabling push notifications, because you\'re on the Web.');
            return;
        }
        if (this.token !== null) {
            console.log('FirebaseMessageService: push notifications were already initialized.');
            return;
        }
        try {
            await this.addListeners();
            let permStatus = await PushNotifications.checkPermissions();
            if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions();
            }
            if (permStatus.receive !== 'granted') {
                throw new Error('User denied permissions!');
            }

            await PushNotifications.register();

        } catch (error) {
            console.warn('FirebaseMessageService: Failed to register with Firebase Push Notifications service, because of an error.', error);
        }
    }

    public getToken(): string | null {
        return this.token;
    }

    /**
     * Check whether Firebase Push Notifications are available and operational
     */
    public isOperational(): boolean {
        return this.token != null && this.token !== '';
    }

    private async addListeners() {
        this.hRegistration = this.hRegistration || await PushNotifications.addListener('registration',
            (token: Token) => {
                // Security: Token value is not logged to prevent exposure in logs/debugging tools
                this.token = token.value;
                // Use debounced subject to prevent multiple rapid registration events
                this.registrationSubject.next(this.token);
            }
        );
        this.hRegistrationError = this.hRegistrationError || await PushNotifications.addListener('registrationError',
            (error: any) => {
                console.error('FirebaseMessagingService: error on registration');
                console.error(error);
                this.onRegistrationError.emit(error);
            }
        );
        this.hPushNotificationReceived = this.hPushNotificationReceived || await PushNotifications.addListener('pushNotificationReceived',
            (notification: PushNotificationSchema) => {
                console.log('FirebaseMessagingService: push received', notification);
                if (notification.body == null) {
                    this.onDataMessageReceived.emit(notification);
                } else {
                    this.onPushNotificationReceived.emit(notification);
                }
            }
        );
        this.hPushNotificationActionPerformed = this.hPushNotificationActionPerformed || await PushNotifications.addListener('pushNotificationActionPerformed',
            (action: ActionPerformed) => {
                console.log('FirebaseMessagingService: push notification action performed', action);
                this.onPushNotificationActionPerformed.emit(action);
            }
        );
    }
}

export interface FirebaseDataMessage {
    id: string;
    data: any;
}
